import { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ImageIcon, QrCode, Nfc } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * BW12 — alternative wallet capture flows. Renders 3 mode cards:
 *
 * 1. Screenshot upload — read existing pass/ticket/QR as image, store on doc
 * 2. QR scan — open camera, decode, store payload on doc
 * 3. NFC tap — Web NFC where supported (Android Chrome); graceful note on iOS
 */

type Props = {
  category: string;
  kind: string;
  title: string;
  onSaved: (documentId: string) => void;
};

export function CaptureModePicker({ category, kind, title, onSaved }: Props) {
  const [mode, setMode] = useState<"none" | "screenshot" | "qr" | "nfc">("none");
  const supportsNFC = typeof window !== "undefined" && "NDEFReader" in window;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <h3 style={{ margin: 0, fontSize: "var(--type-body)", fontWeight: 500 }}>
        Capture from existing pass
      </h3>
      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
        <ModeCard
          icon={<ImageIcon size={18} color="var(--accent)" />}
          label="Screenshot"
          hint="Photo of an existing pass / boarding pass / ticket"
          onClick={() => setMode("screenshot")}
          active={mode === "screenshot"}
        />
        <ModeCard
          icon={<QrCode size={18} color="var(--accent)" />}
          label="QR code"
          hint="Scan with camera"
          onClick={() => setMode("qr")}
          active={mode === "qr"}
        />
        <ModeCard
          icon={<Nfc size={18} color={supportsNFC ? "var(--accent)" : "var(--fg-3)"} />}
          label="NFC tag"
          hint={supportsNFC ? "Tap NFC card on phone" : "Android Chrome only"}
          onClick={() => supportsNFC && setMode("nfc")}
          active={mode === "nfc"}
          disabled={!supportsNFC}
        />
      </div>

      {mode === "screenshot" && (
        <ScreenshotMode category={category} kind={kind} title={title} onSaved={onSaved} onCancel={() => setMode("none")} />
      )}
      {mode === "qr" && (
        <QrScanMode category={category} kind={kind} title={title} onSaved={onSaved} onCancel={() => setMode("none")} />
      )}
      {mode === "nfc" && (
        <NfcMode category={category} kind={kind} title={title} onSaved={onSaved} onCancel={() => setMode("none")} />
      )}
    </div>
  );
}

function ModeCard({
  icon,
  label,
  hint,
  onClick,
  active,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
  active: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: "left",
        padding: 12,
        border: `1px solid ${active ? "var(--accent)" : "var(--hair-2)"}`,
        borderRadius: 10,
        background: active ? "var(--accent-wash)" : "transparent",
        color: disabled ? "var(--fg-3)" : "var(--fg)",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        fontFamily: "inherit",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--type-body-s)", fontWeight: 500 }}>
        {icon} {label}
      </div>
      <span style={{ fontSize: 11, color: "var(--fg-3)" }}>{hint}</span>
    </button>
  );
}

function ScreenshotMode({ category, kind, title, onSaved, onCancel }: { category: string; kind: string; title: string; onSaved: (id: string) => void; onCancel: () => void }) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error("Choose a screenshot first");
      const { data, error } = await supabase
        .from("document")
        .insert({
          user_id: userId,
          category,
          kind: kind || "captured",
          title: title || "Captured pass",
          screenshot_url: preview,
        } as never)
        .select("id")
        .single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      onSaved(id);
    },
  });

  return (
    <div style={panel}>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const r = new FileReader();
          r.onload = () => setPreview(r.result as string);
          r.readAsDataURL(f);
        }}
      />
      {preview && (
        <img src={preview} alt="Captured" style={{ width: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 8 }} />
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onCancel} style={ghost}>Cancel</button>
        <button onClick={() => save.mutate()} disabled={!preview || save.isPending} style={primary}>
          {save.isPending ? "Saving…" : "Save document"}
        </button>
      </div>
    </div>
  );
}

function QrScanMode({ category, kind, title, onSaved, onCancel }: { category: string; kind: string; title: string; onSaved: (id: string) => void; onCancel: () => void }) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [decoded, setDecoded] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let detector: BarcodeDetectorLike | null = null;
    let raf = 0;

    async function start() {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play();
        }
        const BarcodeDetectorCtor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtorLike }).BarcodeDetector;
        if (!BarcodeDetectorCtor) {
          setErr("BarcodeDetector not supported. Use the screenshot mode and re-display the QR.");
          return;
        }
        detector = new BarcodeDetectorCtor({ formats: ["qr_code"] });
        const tick = async () => {
          if (cancelled || !videoRef.current || !detector) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0) {
              setDecoded(codes[0].rawValue);
              return;
            }
          } catch {
            /* ignore frame */
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e) {
        setErr(`Camera failed: ${(e as Error).message}`);
      }
    }
    start();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = useMutation({
    mutationFn: async () => {
      if (!decoded) throw new Error("No QR decoded yet");
      const fmt = inferQrFormat(decoded);
      const { data, error } = await supabase
        .from("document")
        .insert({
          user_id: userId,
          category,
          kind: kind || "captured",
          title: title || "Captured QR",
          qr_payload: decoded,
          qr_format: fmt,
        } as never)
        .select("id")
        .single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      onSaved(id);
    },
  });

  return (
    <div style={panel}>
      {err && <p style={{ color: "var(--severity-bad)", fontSize: 12 }}>{err}</p>}
      <video ref={videoRef} playsInline muted style={{ width: "100%", maxHeight: 320, borderRadius: 8, background: "#000" }} />
      {decoded && (
        <div style={{ background: "var(--accent-wash)", padding: 10, borderRadius: 8, fontSize: 12, fontFamily: "var(--font-mono)" }}>
          {decoded.length > 200 ? decoded.slice(0, 200) + "…" : decoded}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onCancel} style={ghost}>Cancel</button>
        <button onClick={() => save.mutate()} disabled={!decoded || save.isPending} style={primary}>
          {save.isPending ? "Saving…" : "Save document"}
        </button>
      </div>
    </div>
  );
}

function NfcMode({ category, kind, title, onSaved, onCancel }: { category: string; kind: string; title: string; onSaved: (id: string) => void; onCancel: () => void }) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const [tagId, setTagId] = useState<string | null>(null);
  const [records, setRecords] = useState<unknown[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let reader: NDEFReaderLike | null = null;
    async function start() {
      try {
        const Ctor = (window as unknown as { NDEFReader?: NDEFReaderCtor }).NDEFReader;
        if (!Ctor) {
          setErr("Web NFC unsupported on this browser. Use Android Chrome.");
          return;
        }
        reader = new Ctor();
        await reader.scan();
        reader.onreading = (event: NDEFReadingEvent) => {
          setTagId(event.serialNumber ?? null);
          const recs: unknown[] = [];
          for (const r of event.message.records) {
            recs.push({
              recordType: r.recordType,
              mediaType: r.mediaType,
              data: r.data ? new TextDecoder().decode(r.data) : null,
            });
          }
          setRecords(recs);
        };
      } catch (e) {
        setErr(`NFC scan failed: ${(e as Error).message}`);
      }
    }
    start();
    return () => {
      // No NDEFReader.stop API in spec; reader is GC'd.
    };
  }, []);

  const save = useMutation({
    mutationFn: async () => {
      if (records.length === 0) throw new Error("Tap an NFC tag first");
      const { data, error } = await supabase
        .from("document")
        .insert({
          user_id: userId,
          category,
          kind: kind || "captured",
          title: title || "NFC tag",
          nfc_payload: records,
          nfc_tag_id: tagId,
        } as never)
        .select("id")
        .single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      onSaved(id);
    },
  });

  return (
    <div style={panel}>
      {err && <p style={{ color: "var(--severity-bad)", fontSize: 12 }}>{err}</p>}
      <p style={{ fontSize: "var(--type-body-s)", color: "var(--fg-2)" }}>
        Tap your NFC card or tag to your phone…
      </p>
      {tagId && (
        <div style={{ fontSize: 12, color: "var(--fg-3)" }}>Tag UID: <code>{tagId}</code></div>
      )}
      {records.length > 0 && (
        <pre style={{ fontSize: 11, background: "var(--chip)", padding: 10, borderRadius: 8, margin: 0, overflow: "auto" }}>
          {JSON.stringify(records, null, 2)}
        </pre>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onCancel} style={ghost}>Cancel</button>
        <button onClick={() => save.mutate()} disabled={records.length === 0 || save.isPending} style={primary}>
          {save.isPending ? "Saving…" : "Save document"}
        </button>
      </div>
    </div>
  );
}

function inferQrFormat(payload: string): string {
  if (/^https?:\/\//i.test(payload)) return "url";
  if (/^WIFI:/i.test(payload)) return "wifi";
  if (/^BEGIN:VCARD/i.test(payload)) return "vcard";
  if (/^MECARD:/i.test(payload)) return "meCard";
  if (/^smsto:/i.test(payload)) return "sms";
  if (/^mailto:/i.test(payload)) return "email";
  if (/^geo:/i.test(payload)) return "geo";
  if (/^BCD/i.test(payload)) return "epc";
  return "plain";
}

const panel: React.CSSProperties = {
  marginTop: 12,
  padding: 12,
  border: "1px solid var(--hair)",
  borderRadius: 10,
  background: "var(--bg-2)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const primary: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-on)",
  padding: "10px 16px",
  borderRadius: 8,
  border: "none",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
};

const ghost: React.CSSProperties = {
  background: "transparent",
  color: "var(--fg)",
  padding: "10px 16px",
  borderRadius: 8,
  border: "1px solid var(--hair-2)",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
};

// ----------------------------------------------------------------------------
// Web NFC + BarcodeDetector type shims (Web NFC isn't in TS DOM lib yet).
// ----------------------------------------------------------------------------

type BarcodeDetectorCtorLike = new (init?: { formats?: string[] }) => BarcodeDetectorLike;
type BarcodeDetectorLike = { detect: (source: HTMLVideoElement) => Promise<{ rawValue: string }[]> };

type NDEFReaderCtor = new () => NDEFReaderLike;
type NDEFReaderLike = {
  scan: () => Promise<void>;
  onreading: ((event: NDEFReadingEvent) => void) | null;
};

type NDEFReadingEvent = {
  serialNumber?: string;
  message: { records: NDEFRecord[] };
};

type NDEFRecord = {
  recordType: string;
  mediaType?: string;
  data?: ArrayBufferLike;
};
